import React from 'react';
import LegalLayout from './LegalLayout';
import { COMPANY } from '../../config/company';

function Section({ num, title, children }) {
  return (
    <section>
      <h2 className="text-lg font-bold text-foreground mb-3 flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/20 border border-primary/30 text-primary text-xs font-bold flex-shrink-0">
          {num}
        </span>
        {title}
      </h2>
      <div className="pl-10 space-y-2 text-sm">{children}</div>
    </section>
  );
}

export default function CGV({ onOpenWizard }) {
  return (
    <LegalLayout title="Conditions Générales de Vente (CGV)" onOpenWizard={onOpenWizard}>

      <p className="text-sm bg-card/40 border border-border/30 rounded-xl px-4 py-3">
        Les présentes Conditions Générales de Vente s'appliquent à toutes les prestations de services
        conclues entre <strong className="text-foreground">{COMPANY.legalName}</strong> et ses clients professionnels (B2B).
        Toute commande implique l'acceptation sans réserve des présentes CGV.
      </p>

      <Section num="1" title="Objet et champ d'application">
        <p>
          Les présentes CGV définissent les conditions dans lesquelles {COMPANY.legalName}
          (ci-après « le Prestataire ») fournit à ses clients professionnels (ci-après « le Client »)
          des prestations de conseil, d'audit et d'accompagnement en migration d'identité et de gestion
          des accès (IAM), notamment vers la plateforme Auth0.
        </p>
        <p>
          Ces CGV prévalent sur tout document du Client, notamment ses conditions générales d'achat,
          sauf accord écrit contraire signé par les deux parties.
        </p>
      </Section>

      <Section num="2" title="Devis et commande">
        <p>
          Toute prestation fait l'objet d'un devis écrit, valable <strong className="text-foreground">30 jours</strong> à
          compter de sa date d'émission. Le devis devient ferme et définitif à réception par le Prestataire
          d'un bon de commande signé ou d'un e-mail de confirmation du Client.
        </p>
        <p>
          Toute modification de périmètre en cours de mission fera l'objet d'un avenant au contrat.
        </p>
      </Section>

      <Section num="3" title="Tarifs et conditions de paiement">
        <p>
          Les prestations sont facturées selon les tarifs en vigueur au jour de la commande,
          exprimés en euros HT. La TVA applicable est celle en vigueur à la date de facturation.
        </p>
        <div className="bg-card/40 rounded-lg p-3 border border-border/30 space-y-1">
          <p><strong className="text-foreground">Modalités de paiement standard :</strong></p>
          <ul className="list-disc pl-4 space-y-1">
            <li>30 % à la commande (acompte)</li>
            <li>40 % à mi-parcours (jalons définis au contrat)</li>
            <li>30 % à la livraison finale</li>
          </ul>
        </div>
        <p>
          Règlement par virement bancaire sous <strong className="text-foreground">30 jours</strong> date de facture,
          sauf accord contraire.
        </p>
        <p>
          En cas de retard de paiement, des pénalités de retard seront appliquées au taux directeur
          de la BCE majoré de 10 points, ainsi qu'une indemnité forfaitaire de recouvrement de 40 €
          (art. L.441-10 C. com.).
        </p>
      </Section>

      <Section num="4" title="Obligations du Prestataire">
        <p>
          Le Prestataire s'engage à fournir les prestations décrites dans le devis avec le soin
          et la compétence requis, dans le respect des délais convenus.
        </p>
        <p>
          Le Prestataire est soumis à une <strong className="text-foreground">obligation de moyens</strong> et non de résultat,
          sauf disposition contraire expressément mentionnée dans le contrat.
        </p>
        <p>
          Le Prestataire s'engage à la confidentialité absolue sur les informations techniques et
          commerciales du Client (clause de confidentialité incluse dans tout contrat).
        </p>
      </Section>

      <Section num="5" title="Obligations du Client">
        <p>Le Client s'engage à :</p>
        <ul className="list-disc pl-4 space-y-1">
          <li>Fournir en temps utile toutes les informations et accès nécessaires à la réalisation de la mission</li>
          <li>Désigner un interlocuteur technique disponible et disposant des droits nécessaires</li>
          <li>Régler les factures dans les délais convenus</li>
          <li>Ne pas solliciter directement les collaborateurs du Prestataire pendant la mission et les 12 mois suivants</li>
        </ul>
      </Section>

      <Section num="6" title="Propriété intellectuelle">
        <p>
          Les livrables (rapports, scripts, configurations) produits dans le cadre de la mission
          deviennent la propriété du Client à complet paiement de la prestation.
        </p>
        <p>
          Les outils, méthodologies et savoir-faire propriétaires du Prestataire restent
          sa propriété exclusive.
        </p>
      </Section>

      <Section num="7" title="Responsabilité et garantie">
        <p>
          La responsabilité du Prestataire est limitée au montant HT facturé au titre de la prestation
          concernée. Le Prestataire ne saurait être tenu responsable des dommages indirects
          (perte de données, perte d'exploitation, préjudice commercial).
        </p>
        <p>
          Le Client est seul responsable de la validation des livrables en recette/staging
          avant toute mise en production.
        </p>
      </Section>

      <Section num="8" title="Résiliation">
        <p>
          En cas de manquement grave par l'une des parties à ses obligations, l'autre partie peut
          résilier le contrat par lettre recommandée avec accusé de réception, après mise en demeure
          restée sans effet sous <strong className="text-foreground">15 jours ouvrés</strong>.
        </p>
        <p>
          En cas de résiliation à l'initiative du Client sans faute du Prestataire, les prestations
          déjà réalisées sont dues intégralement, augmentées d'une indemnité de résiliation
          de <strong className="text-foreground">20 % du montant HT restant</strong>.
        </p>
      </Section>

      <Section num="9" title="Force majeure">
        <p>
          Les parties ne pourront être tenues responsables de l'inexécution de leurs obligations
          résultant d'un événement de force majeure au sens de l'article 1218 du Code civil
          (pandémie, cyberattaque étatique, catastrophe naturelle, etc.).
        </p>
      </Section>

      <Section num="10" title="Droit applicable et règlement des litiges">
        <p>
          Les présentes CGV sont soumises au droit français.
          En cas de litige, les parties s'engagent à rechercher une solution amiable.
          À défaut, le litige sera soumis aux tribunaux compétents de <strong className="text-foreground">Paris</strong>.
        </p>
        <p>
          Pour tout renseignement :{' '}
          <a href={COMPANY.emailHref} className="text-primary hover:underline">{COMPANY.email}</a>
        </p>
      </Section>

    </LegalLayout>
  );
}
